import { useMemo } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import {
  axisClasses,
  LineChart as MuiLineChart,
  ChartsLegend,
  type ChartsLegendProps,
} from '@mui/x-charts';
import { labelMarkClasses } from '@mui/x-charts/ChartsLabel';
import i18next from 'i18next';
import { bloxMarketingColors } from '../../../../themes/constants';
import { useWidgetContext } from 'components/Dashboard/Widget';
import {
  CHART_HEIGHT,
  CHART_MARGIN_TOP_DESK,
  CHART_MARGIN_TOP_MOBI,
} from 'components/Dashboard/shared/constants';
import WidgetError from 'components/Dashboard/components/WidgetError';
import { formatChartValue, getDatapointFormat } from 'components/Dashboard/shared/utils';
import {
  TBloxAxisConfig,
  TBloxAxisConfigMultiple,
  TChartSeriesItem,
} from 'components/Dashboard/shared/types';
import { toTitleCase } from 'utility/functions';

const CustomLegend = (props: ChartsLegendProps) => {
  return (
    <div data-testid="line-chart-legend">
      <ChartsLegend {...props} />
    </div>
  );
};

const LineChart = () => {
  const { widget, data, config } = useWidgetContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Responsive height: smaller on mobile devices
  const height = isMobile ? 280 : isTablet ? 320 : CHART_HEIGHT;
  const locale = i18next.language;

  const lineChart = useMemo(() => {
    const initialData = {
      dataset: [],
      series: [],
      xAxis: [],
    };

    if (!widget || !data?.results?.length || data?.total < 1) return initialData;

    const dataset = data?.results;

    // Interpret the module to correlate with the dataset.
    const displayOptions = config;

    // Determine if the chart area should be filled.
    const isAreaFilled = displayOptions?.subtype === 'area';

    // =========================================
    // Y-Axis
    // =========================================

    // Get the y-axis display configuration.
    const yAxisConfig: TBloxAxisConfig = !!displayOptions?.['y-col']?.fields
      ? (displayOptions?.['y-col'] as TBloxAxisConfigMultiple)
      : Array.isArray(displayOptions?.['y-col'])
        ? displayOptions?.['y-col']
        : [displayOptions?.['y-col']];

    const yDataKeys =
      Array.isArray(yAxisConfig) && !!yAxisConfig?.length
        ? yAxisConfig
        : (yAxisConfig as TBloxAxisConfigMultiple)?.fields;

    const series = yDataKeys.map((dataKey: string) => {
      const currentModuleData: TChartSeriesItem = widget.module?.data?.[dataKey];
      const format = getDatapointFormat(currentModuleData);
      const label = currentModuleData?.label;

      const valueFormatter = (value: any) => {
        if (dataKey.startsWith('usage_in_')) {
          const unit = dataKey.split('_')[2].toUpperCase();
          const formattedValue = formatChartValue({
            value,
            format: 'number',
            locale,
          });
          return `${toTitleCase(String(formattedValue ?? ''))}${unit}`;
        }
        return formatChartValue({
          value,
          format,
          dateFormat: currentModuleData?.date_format,
          locale,
        });
      };

      const data = dataset.map((item: any) => item[dataKey]);

      return {
        id: `line-y-axis--${widget.id}-${dataKey}`,
        showMark: false,
        connectNulls: true,
        data,
        label,
        valueFormatter,
        area: isAreaFilled,
      };
    });

    // =========================================
    // X-Axis
    // =========================================

    // Get the x-axis display configuration.
    const xAxisDisplayFormat: string | undefined = displayOptions?.['x-display-format'];

    const xAxisKeys: TBloxAxisConfig = !!displayOptions?.['x-col']?.fields
      ? (displayOptions?.['x-col'] as TBloxAxisConfigMultiple)
      : Array.isArray(displayOptions?.['x-col'])
        ? displayOptions?.['x-col']
        : [displayOptions?.['x-col']];

    // Form the xAxis MUI property (x-col data refs).
    const xAxisDataKeys =
      Array.isArray(xAxisKeys) && !!xAxisKeys?.length
        ? xAxisKeys
        : (xAxisKeys as TBloxAxisConfigMultiple)?.fields;

    const xAxis = xAxisDataKeys.map((dataKey: string) => {
      const currentSeries: TChartSeriesItem = widget.module?.data?.[dataKey];
      const axisLabel = currentSeries?.label;
      const format = getDatapointFormat(currentSeries, xAxisDisplayFormat);
      const dateFormat = !xAxisDisplayFormat
        ? currentSeries?.date_format
        : xAxisDisplayFormat;

      const valueFormatter = (value = '') =>
        formatChartValue({ value, format, dateFormat, locale });

      // Keep raw values in axis domain so duplicate display labels do not collapse points.
      const data = dataset.map((item: any) => item[dataKey]);

      // CMSADMIN-1973: Use 'band' scale for single data point, guarantees tooltip date label.
      const scaleType = dataset.length === 1 ? ('band' as const) : ('point' as const);

      return {
        data,
        id: `line-x-axis--${widget.id}-${dataKey}`,
        label: axisLabel,
        scaleType,
        valueFormatter,
      };
    });

    // If the series and xAxis are valid, return the data.
    if (series?.length && xAxis?.length) {
      return {
        dataset,
        series,
        xAxis,
      };
    }

    return initialData;
  }, [widget, data?.results, data?.total, config, locale]);

  if (!lineChart?.dataset?.length) {
    return <WidgetError type="noData" />;
  }

  return (
    <Box
      data-testid={`line-chart-widget--${widget?.id}`}
      sx={{
        width: '100%',
      }}
    >
      <MuiLineChart
        height={height}
        dataset={lineChart.dataset}
        series={lineChart.series}
        xAxis={lineChart.xAxis}
        // Responsive margins: reduce left margin on mobile for more chart space
        margin={{
          // 50 to show 6-fig y-axis labels on mobile.
          left: isMobile ? 50 : isTablet ? 56 : 72,
          right: isMobile ? 8 : 16,
          top: isMobile ? CHART_MARGIN_TOP_MOBI : CHART_MARGIN_TOP_DESK,
          bottom: isMobile ? 8 : 40,
        }}
        colors={Object.values(bloxMarketingColors)}
        sx={{
          [`.${axisClasses.left} .${axisClasses.label}`]: {
            transform: isMobile ? 'translate(-12px, 0)' : 'translate(-20px, 0)',
          },
          // Style adjustments for better mobile readability
          [`.${axisClasses.left} .${axisClasses.tickLabel}`]: {
            fontSize: isMobile ? '0.7rem' : '0.875rem',
          },
          [`.${axisClasses.bottom} .${axisClasses.tickLabel}`]: {
            fontSize: isMobile ? '0.7rem' : '0.875rem',
          },
          '.MuiChartsLegend-series': {
            '.MuiChartsLegend-mark': {
              rx: 100,
              maxWidth: isMobile ? 16 : 20,
            },
          },
        }}
        slots={{
          legend: CustomLegend,
        }}
        slotProps={{
          legend: {
            // Position legend at top on mobile to avoid horizontal scrolling.
            position: {
              vertical: 'top',
              horizontal: isMobile ? 'end' : 'center',
            },
            direction: 'horizontal',
            sx: {
              p: isMobile ? 0 : 8,
              gap: isMobile ? 8 : 12,
              [`.${labelMarkClasses}`]: {
                fontSize: isMobile ? 11 : 13,
              },
            },
          },
        }}
        hideLegend={isMobile}
      />
    </Box>
  );
};

export default LineChart;
